import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextareaAutosize,
  TextField,
  Typography,
} from '@material-ui/core';
import { green, red } from '@material-ui/core/colors';
import ClearIcon from '@material-ui/icons/Clear';
import DoneIcon from '@material-ui/icons/Done';
import { Canceler } from 'axios';
import { cloneDeep } from 'lodash';
import moment, { DurationInputArg2 } from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockPicker } from 'react-color';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { LinkedSegments } from '../../../components/Curation/Sections';
import { Help } from '../../../components/Help';
import { ImageUpload } from '../../../components/ImageUpload';
import StoryList from '../../../components/StoryList';
import { SubmitControl } from '../../../components/SubmitControl';
import useBackdrop from '../../../hooks/useBackdrop';
import {
  editAutomatedCollection,
  getAutomatedCollection,
  getAutomatedCollectionQueryResult,
  saveAutomatedCollection,
} from '../../../services/api/AutomatedCollection';
import api from '../../../services/api/base';
import { getDisplayStyles } from '../../../services/api/DisplayStyle';
import { uploadFile } from '../../../services/api/S3';
import {
  CollectionType,
  ICollectionCreator,
  ICollectionDisplayType,
  IDisplayStyle,
  IHomeset,
  IntervalType,
  ISegmentCuration,
  IStory,
  IStoryGenreTextMap,
} from '../../../services/types';
import { convertPeriodWithTimezone, getNextFireDate, getPathnameFromURI, replace } from '../../../services/utils';
import useStyles from './styles';

interface IAutomatedCollectionViewProps {
  trackingId?: string;
  onCreated?: (data: ICollectionCreator) => void;
  onError?: (error: Error) => void;
}

const AutomatedCollectionView = (props: IAutomatedCollectionViewProps): JSX.Element => {
  const { trackingId, onCreated, onError } = props;
  const DefaultImage = `featured/collection_default_banner.png`;

  const classes = useStyles();
  const backdrop = useBackdrop();

  // states
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [pendingUpload, setPendingUpload] = useState<boolean>();
  const [label, setLabel] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [internalTitle, setInternalTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [image, setImage] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [bgColor, setBgColor] = useState<string>('#ff008a');
  const [contentLayout, setContentLayout] = useState<number>(2);
  const [contentType, setContentType] = useState<string>('fictionSerials');
  const [showRanking, setShowRanking] = useState<boolean>(false);
  const [randomOrder, setRandomOrder] = useState<boolean>(false);
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
  const [selectedDisplayStyleId, setSelectedDisplayStyleId] = useState<number>();
  const [displayStyles, setDisplayStyles] = useState<IDisplayStyle[]>([]);
  const [linkedHomesets, setLiveHomesets] = useState<IHomeset[]>([]);
  const [intervalType, setIntervalType] = useState<IntervalType>(IntervalType.Hourly);
  const [intervalValue, setIntervalValue] = useState<number>();
  const [period, setPeriod] = useState<number>();
  const [runImmediately, setRunImmediately] = useState<boolean>(false);
  const [sqlError, setSqlError] = useState<boolean>();
  const [validationMessages, setValidationMessages] = useState<string[]>(['Nothing changed']);
  const [pendingTest, setPendingTest] = useState<boolean>(false);
  const [sql, setSql] = useState<string>('');
  const [testStories, setTestStories] = useState<IStory[]>();
  const [testResultStatus, setTestResultStatus] = useState<number>();
  const [currentTrackingId, setCurrentTrackingId] = useState<string>('');
  const [pendingInitialLoading, setPendingInitialLoading] = useState<boolean>(false);
  const [nextAt, setNextAt] = useState<string | undefined>();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [nextFireDescriptions, setNextFireDescriptions] = useState<string[]>();
  const [linkedCurations, setLinkedCurations] = useState<ISegmentCuration[]>();

  // refs
  const displayStylesCanceler = useRef<Canceler>(() => undefined);
  const collectionsCanceler = useRef<Canceler>(() => undefined);
  const originalData = useRef<Partial<ICollectionCreator>>({
    label: '',
    title: '',
    internalTitle: '',
    description: '',
    coverUrl: '',
    backgroundColor: '#ff008a',
    layoutColumns: 2,
    type: CollectionType.FictionSerials,
    showRanking: false,
    randomOrder: false,
    immediately: false,
    intervalType: IntervalType.Hourly,
    intervalValue: 0,
    period: 0,
    sql: '',
    displayType: ICollectionDisplayType.General,
    isOnboarding: false,
  });

  // handlers
  const onFileLoaded = async (file: File, blob: Blob, imageContent: string) => {
    setImage(imageContent);
    setFileName(file.name);

    try {
      setPendingUpload(true);
      const result = await uploadFile('collection_image.jpg', blob);

      if (result?.data && result?.status === 200) {
        setImage(result.data.url);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPendingUpload(false);
    }
  };

  const onKeyDownImage = (e: React.KeyboardEvent) => {
    e.preventDefault();

    if (e.key === 'Backspace') {
      setFileName('');
      setImage('');
    }
  };

  const handleChangeIntervalType = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const type = e.target.value;

    setNextAt(undefined);
    setIntervalType(type as IntervalType);
    setIntervalValue(undefined);
    setPeriod(undefined);
  };

  const handleChangeIntervalValueType = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = e.target.value;

    setNextAt(undefined);
    setIntervalValue(Number(value));
    setPeriod(undefined);
  };

  const handleChangeIntervalPeriod = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const value = e.target.value;

    setNextAt(undefined);
    setPeriod(Number(value));
  };

  const stateToPayload = (): Partial<ICollectionCreator> => {
    let calculatedPeriod = period;

    if (intervalType === IntervalType.Daily) {
      // Local time zone to UTC (server)
      calculatedPeriod = Number(
        moment(period, 'H')
          .utc()
          .format('H'),
      );
    }

    return {
      displayType: ICollectionDisplayType.General,
      label,
      title,
      internalTitle,
      description,
      layoutColumns: contentLayout,
      backgroundColor: bgColor,
      coverUrl: getPathnameFromURI(image),
      type: contentType as CollectionType,
      showRanking,
      randomOrder,
      sql,
      intervalType: intervalType as IntervalType,
      intervalValue,
      period: calculatedPeriod,
      immediately: runImmediately,
      displayStyleId: selectedDisplayStyleId,
      isOnboarding,
    };
  };

  const handleClickSubmit = async () => {
    const payload: Partial<ICollectionCreator> = stateToPayload();
    if (testStories) {
      payload.storyGenreTextMaps = testStories.reduce<IStoryGenreTextMap[]>((acc, cur) => {
        const { id: storyId, storyGenreText } = cur;

        if (storyGenreText) {
          acc.push({
            storyId,
            storyGenreText,
          });
        }

        return acc;
      }, []);
    }

    try {
      backdrop.show();

      const result = isEditing ? await editAutomatedCollection(currentTrackingId, payload) : await saveAutomatedCollection(payload);

      if (api.isResponseSuccess(result)) {
        if (!currentTrackingId && originalData.current) {
          setStatesFromData(originalData.current);
        }

        if (currentTrackingId) {
          setIsEditing(false);
          fetchData();
        }

        if (onCreated) {
          onCreated(result.data as ICollectionCreator);
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
  };

  // apis
  const setStatesFromData = (data: Partial<ICollectionCreator>) => {
    data.label !== undefined && setLabel(data?.label);
    data.title !== undefined && setTitle(data?.title);
    data.internalTitle !== undefined && setInternalTitle(data.internalTitle);
    data.description !== undefined && setDescription(data?.description);
    setFileName(data.coverUrl || '');
    setImage(data.coverUrl || '');
    setBgColor(data.backgroundColor || '#ff008a');
    data.showRanking !== undefined && setShowRanking(data?.showRanking);
    data.randomOrder !== undefined && setRandomOrder(data?.randomOrder);
    data.layoutColumns !== undefined && setContentLayout(data?.layoutColumns);
    data.type !== undefined && setContentType(data?.type);
    data.isOnboarding !== undefined && setIsOnboarding(data.isOnboarding);

    data.homesets !== undefined && setLiveHomesets(data?.homesets);
    setIntervalType(data.intervalType as IntervalType);
    data.intervalValue !== undefined && setIntervalValue(data.intervalValue);

    if (data.period !== undefined) {
      setPeriod(data.period);
    }

    setSelectedDisplayStyleId(data.displayStyleId);
    data.sql !== undefined && setSql(data?.sql);

    if (data.nextAt !== undefined) {
      setNextAt(data.nextAt);
    }

    if (data.isActive !== undefined) {
      setIsActive(data.isActive);
    }
    setSqlError(false);
    setPendingTest(false);
    setTestResultStatus(undefined);

    if (data.trackingId !== undefined) {
      setCurrentTrackingId(data.trackingId);
    }
    setLinkedCurations(data.linkedCurations);
  };

  const fetchDisplayStyles = async () => {
    displayStylesCanceler.current();

    try {
      const [fetchPromise, cancel] = getDisplayStyles();
      displayStylesCanceler.current = cancel;

      const result = await fetchPromise;

      if (result && result.status === 200 && result.data) {
        const { items } = result.data;
        setDisplayStyles(items);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const fetchData = useCallback(async () => {
    if (!trackingId) {
      return;
    }

    collectionsCanceler.current();

    try {
      setPendingInitialLoading(true);
      const [fetchPromise, cancel] = getAutomatedCollection(trackingId);
      collectionsCanceler.current = cancel;
      const result = await fetchPromise;

      if (result.status === 200) {
        const data = result.data;

        originalData.current = data;
        setStatesFromData(data);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
      console.error('e', e);

      if (onError) {
        onError(e);
      }
    } finally {
      setPendingInitialLoading(false);
    }
  }, [onError, trackingId]);

  const testSQL = async (testSql: string = sql) => {
    setPendingTest(true);

    try {
      const result = await getAutomatedCollectionQueryResult(testSql);

      if (result.status === 201) {
        const data = result.data.stories;
        // Test passed
        setTestStories(data);
      }

      setTestResultStatus(result.status);
      setPendingTest(false);
    } catch (e) {
      console.error(e);

      if (onError) {
        onError(e);
      }

      setTestResultStatus(500);
      setPendingTest(false);
    }
  };

  // watches
  useEffect(() => {
    fetchDisplayStyles();

    return () => displayStylesCanceler.current();
  }, []);

  useEffect(() => {
    fetchData();

    return () => collectionsCanceler.current();
  }, [fetchData]);

  useEffect(() => {
    if (!intervalValue) {
      setNextFireDescriptions([]);
      return;
    }

    if (intervalType !== 'hour' && period === undefined) {
      setNextFireDescriptions([]);
      return;
    }

    const nextAts = [];
    const nextFireDate = getNextFireDate(intervalType, intervalValue, period, 13, nextAt);

    if (nextFireDate) {
      for (let i = 0; i < 5; i++) {
        nextAts.push(nextFireDate.format('LLLL z'));
        nextFireDate.add(intervalValue, intervalType as DurationInputArg2);
      }
    }

    if (runImmediately) {
      nextAts.unshift(moment().format('LLLL z'));
    } else if (nextAt) {
      const formattedNextAt = moment(nextAt).format('LLLL z');

      if (formattedNextAt !== nextAts[0]) {
        nextAts.unshift(formattedNextAt);
      }
    }

    setNextFireDescriptions(nextAts.slice(0, 5));
  }, [intervalValue, period, runImmediately, nextAt, intervalType]);

  useEffect(() => {
    setValidationMessages(isEditing ? ['Nothing changed'] : []);
  }, [isEditing]);

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!title) {
      messageSet.add('A title cannot be empty.');
    }

    if (!intervalType) {
      messageSet.add('An interval type cannot be empty.');
    }

    if (!intervalValue) {
      messageSet.add('An interval value cannot be empty.');
    }

    if (intervalType !== 'minute' && intervalType !== 'hour' && period === undefined) {
      messageSet.add('A period cannot be empty.');
    }

    if (!sql) {
      messageSet.add('A sql cannot be empty.');
    }

    if (originalData.current.sql !== sql && testResultStatus !== 201) {
      messageSet.add('a SQL should be passed the test.');
      setSqlError(true);
    }

    const messages = Array.from(messageSet);

    setValidationMessages(messages);
  }, [
    title,
    intervalType,
    intervalValue,
    period,
    sql,
    runImmediately,
    testResultStatus,
    label,
    internalTitle,
    description,
    image,
    bgColor,
    contentLayout,
    showRanking,
  ]);

  // Use to determine repetition cycle.
  const IntervalValueMap = useMemo((): Record<string, number[]> => {
    return {
      minute: Array.from({ length: 30 }, (v, k) => k + 1),
      hour: Array.from({ length: 23 }, (v, k) => k + 1),
      day: Array.from({ length: 29 }, (v, k) => k + 1),
      week: Array.from({ length: 5 }, (v, k) => k + 1),
      month: Array.from({ length: 12 }, (v, k) => k + 1),
    };
  }, []);

  const IntervalPeriodMap = useMemo((): Record<string, number[]> => {
    return {
      minute: [],
      hour: [],
      day: Array.from({ length: 24 }, (v, k) => k), // 0 ~ 23
      week: Array.from({ length: 7 }, (v, k) => k), // Sun ~ Sat
      month: Array.from({ length: 30 }, (v, k) => k + 1), // 1st ~ 31st
    };
  }, []);

  const isReadOnly = isEditing === false && !!currentTrackingId;

  if (pendingInitialLoading) {
    return <div />;
  }

  return (
    <div className={classes.createAutomatedCollectionWrap}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '10px', marginBottom: '16px', paddingLeft: '4px', paddingRight: '22px' }}
      >
        <Typography variant="h6" component="h3" style={{ flex: 1 }}>
          {!currentTrackingId ? (
            'Create Automated Collection'
          ) : (
            <div>
              {originalData.current?.internalTitle && <span className={classes.titleEmphasize}>{`${originalData.current?.internalTitle} : `}</span>}
              {originalData.current?.title}
            </div>
          )}
        </Typography>
        <SubmitControl
          mode={!currentTrackingId ? 'create' : isEditing ? 'edit' : 'view'}
          validationMessages={validationMessages}
          onSubmitClick={() => {
            handleClickSubmit();
          }}
          onEditClick={() => {
            setIsEditing(true);
          }}
          onCancelClick={() => {
            if (originalData.current) {
              setStatesFromData(originalData.current);
            }

            if (isEditing) {
              setIsEditing(false);
            }
          }}
        />
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid item xs={6} className={classes.verticalGrid}>
          <Paper className={classes.contentSettingWrap}>
            <Typography variant="h6" component="h3" className={classes.sectionTitle}>
              Content Setting
            </Typography>

            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                {currentTrackingId && (
                  <Grid item xs={12}>
                    <Box paddingBottom={'30px'}>
                      <Typography style={{ marginBottom: '4px' }}>Tracking Id</Typography>
                      <CopyToClipboard content={currentTrackingId} />
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.infoTextField}
                    label="Is Onboarding Collection?"
                    control={
                      <Checkbox
                        color="primary"
                        disabled={isReadOnly}
                        checked={isOnboarding}
                        onChange={e => {
                          setIsOnboarding(e.target.checked);
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Collection Label"
                    className={classes.infoTextField}
                    value={label}
                    required
                    onChange={e => {
                      if (isReadOnly) return;

                      setLabel(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={
                      <span>
                        {'Collection Title (Internal)'}
                        <Help text="Internal title is for the convenience of the administrator. It is not exposed in the Radish app." />
                      </span>
                    }
                    className={classes.infoTextField}
                    value={internalTitle || ''}
                    onChange={e => {
                      if (isReadOnly) return;

                      setInternalTitle(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Collection Title"
                    className={classes.infoTextField}
                    value={title}
                    required
                    onChange={e => {
                      if (isReadOnly) return;

                      setTitle(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Collection Description"
                    className={classes.infoTextField}
                    value={description}
                    multiline
                    rowsMax={5}
                    required
                    onChange={e => {
                      if (isReadOnly) return;

                      setDescription(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Collection Image (Optional)"
                    className={classes.infoTextField}
                    value={fileName}
                    onKeyDown={onKeyDownImage}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.imageFileName,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ImageUpload onFileLoaded={onFileLoaded} src={image || DefaultImage} readonly={isReadOnly} isPending={pendingUpload} />
                </Grid>
              </Box>

              <Box paddingY={2} width="100%">
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Background Color
                </Typography>
                <Grid item xs={12}>
                  <Box className={classes.colorPicker}>
                    <BlockPicker
                      triangle={'hide'}
                      width={'100%'}
                      color={bgColor}
                      colors={[
                        '#FF008A',
                        '#F47373',
                        '#697689',
                        '#37D67A',
                        '#2CCCE4',
                        '#555555',
                        '#dce775',
                        '#ff8a65',
                        '#ba68c8',
                        '#000000',
                        '#ffffff',
                      ]}
                      onChange={() => {
                        if (isReadOnly) return;
                      }}
                      onChangeComplete={({ hex }) => {
                        if (isReadOnly) return;
                        setBgColor(hex);
                      }}
                    />
                  </Box>
                </Grid>
              </Box>

              <Box paddingY={2} width="100%">
                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Collection Content Layout
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name="columnType"
                    value={contentLayout}
                    onChange={e => {
                      if (isReadOnly) return;

                      setContentLayout(Number(e.target.value));
                    }}
                    className={classes.radioButtonGroups}
                  >
                    <FormControlLabel value={2} control={<Radio color="primary" />} label="Double Column" />
                    <FormControlLabel value={1} control={<Radio color="primary" />} label="Single Column" />
                  </RadioGroup>
                </Grid>

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Content Type
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name="contentType"
                    value={contentType}
                    onChange={e => {
                      setContentType(e.target.value);
                    }}
                    className={classes.radioButtonGroups}
                  >
                    <FormControlLabel
                      value="fictionSerials"
                      control={<Radio color="primary" disabled={Boolean(currentTrackingId)} />}
                      label="Stories"
                    />
                    <FormControlLabel
                      value="chatStories"
                      control={<Radio color="primary" disabled={Boolean(currentTrackingId)} />}
                      label="Chat Stories"
                    />
                  </RadioGroup>
                </Grid>

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Collection Display Style
                  </Typography>

                  <Grid item xs={12}>
                    <Select
                      displayEmpty
                      fullWidth
                      value={selectedDisplayStyleId || ''}
                      disabled={isReadOnly || displayStyles.length === 0}
                      onChange={e => setSelectedDisplayStyleId(e.target.value as number)}
                      className={classes.radioButtonGroups}
                    >
                      {displayStyles.map((ds: IDisplayStyle) => (
                        <MenuItem key={ds.id} value={ds.id}>
                          {ds.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Show Ranking
                  </Typography>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="showRanking"
                      value={showRanking}
                      onChange={e => {
                        if (isReadOnly) return;
                        setShowRanking(e.target.value === 'true');
                      }}
                      className={classes.radioButtonGroups}
                    >
                      <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
                      <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                    </RadioGroup>
                  </Grid>
                </Grid>

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Random Order
                  </Typography>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="randomOrder"
                      value={randomOrder}
                      onChange={e => {
                        if (isReadOnly) return;
                        setRandomOrder(e.target.value === 'true');
                      }}
                      className={classes.radioButtonGroups}
                    >
                      <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
                      <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={6} className={classes.verticalGrid}>
          <Grid container direction="column" justify="flex-start" alignItems="stretch">
            {!!currentTrackingId && (
              <Grid item xs={12}>
                <Paper className={classes.contentSettingWrap}>
                  <Typography component="h5" variant="h6">
                    Linked ({linkedCurations?.length || 0})
                  </Typography>
                  <LinkedSegments linkedCurations={linkedCurations} />
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Paper className={classes.contentSettingWrap}>
                <Box>
                  <Typography variant="h6" component="h3" className={classes.flexSectionTitle}>
                    SQL Refresh Interval
                    {currentTrackingId && (
                      <Chip size="small" label={isActive ? 'Active' : 'Inactive'} className={classes.chip} color={isActive ? 'primary' : undefined} />
                    )}
                  </Typography>

                  <Grid container direction="column" justify="flex-start" alignItems="stretch">
                    <Grid item xs={12}>
                      <FormControl className={classes.formControl}>
                        <InputLabel shrink htmlFor="interval-type">
                          Interval Type
                        </InputLabel>
                        <Select
                          value={intervalType}
                          onChange={handleChangeIntervalType}
                          input={<Input name="intervalType" id="interval-type" />}
                          name="intervalType"
                          displayEmpty
                          className={classes.selectEmpty}
                          disabled={isReadOnly}
                        >
                          {process.env.ENV !== 'production' && <MenuItem value={IntervalType.Minutely}>Every Minute (Dev Only)</MenuItem>}
                          <MenuItem value={IntervalType.Hourly}>Hourly</MenuItem>
                          <MenuItem value={IntervalType.Daily}>Daily</MenuItem>
                          <MenuItem value={IntervalType.Weekly}>Weekly</MenuItem>
                          <MenuItem value={IntervalType.Monthly}>Monthly</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl className={classes.formControl}>
                        <InputLabel shrink htmlFor="interval-value">
                          Interval Value
                        </InputLabel>
                        <Select
                          value={intervalValue || ''}
                          onChange={handleChangeIntervalValueType}
                          input={<Input name="intervalValue" id="interval-value" />}
                          name="intervalValue"
                          displayEmpty
                          className={classes.selectEmpty}
                          disabled={isReadOnly}
                        >
                          {IntervalValueMap[intervalType].map((v, i) => (
                            // WARNING: JUST FOR QA TEST CONVENIENCE
                            <MenuItem value={v} key={`i-v-${intervalType}-${i}`}>
                              {v === 1 ? `Every ${intervalType}` : `Every ${v} ${intervalType}s`}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>Determine the repeat cycle.</FormHelperText>
                      </FormControl>

                      <FormControl className={classes.formControl}>
                        <InputLabel shrink htmlFor="period">
                          Period
                        </InputLabel>
                        <Select
                          value={
                            period === undefined
                              ? ''
                              : intervalType === IntervalType.Monthly
                              ? period
                              : convertPeriodWithTimezone(intervalType, period)
                          }
                          onChange={handleChangeIntervalPeriod}
                          input={<Input name="period" id="period" />}
                          name="period"
                          displayEmpty
                          className={classes.selectEmpty}
                          disabled={intervalType === IntervalType.Minutely || intervalType === IntervalType.Hourly || isReadOnly}
                        >
                          {IntervalPeriodMap[intervalType].map((v, i) => {
                            if (intervalType === IntervalType.Minutely || intervalType === IntervalType.Hourly) {
                              return;
                            }

                            if (intervalType === IntervalType.Daily) {
                              return (
                                <MenuItem value={v} key={`i-v-p-${intervalType}-${i}`}>
                                  {moment(v, 'H').format('HH:mm')}
                                </MenuItem>
                              );
                            }

                            if (intervalType === IntervalType.Weekly) {
                              return (
                                <MenuItem value={v} key={`i-v-p-${intervalType}-${i}`}>
                                  {moment(v, 'e').format('dddd')}
                                </MenuItem>
                              );
                            }

                            if (intervalType === IntervalType.Monthly) {
                              return (
                                <MenuItem value={v - 1} key={`i-v-p-${intervalType}-${i}`}>
                                  {moment(v, 'DD').format('Do')}
                                </MenuItem>
                              );
                            }
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {!isReadOnly && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={runImmediately}
                              onChange={() => {
                                setRunImmediately(!runImmediately);
                              }}
                              disabled={isReadOnly}
                            />
                          }
                          label={currentTrackingId ? 'Run query immediately' : 'Activate this automated collection immediately'}
                          disabled={isReadOnly}
                        />
                      </Grid>
                    )}

                    {isActive && nextFireDescriptions && nextFireDescriptions.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" display="block" key={'title'} className={classes.nextFireTitle}>
                          Time to create the next five collections:
                        </Typography>
                        {nextFireDescriptions.map((text, i) => (
                          <Typography variant="caption" key={`nfd-${i}`} display="block" color={i === 0 ? 'secondary' : 'textSecondary'}>
                            {text}
                          </Typography>
                        ))}
                      </Grid>
                    )}

                    {!isActive && (
                      <Typography variant="caption" display="block" key={'title'} className={classes.nextFireTitle}>
                        When a status is active, You can find out when to create your next collection.
                      </Typography>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper className={classes.contentSettingWrap}>
                <Box>
                  <Typography variant="h6" component="h3" className={classes.sectionTitle}>
                    SQL
                  </Typography>
                  <TextareaAutosize
                    rows={10}
                    placeholder="Query here"
                    className={classes.queryTextArea}
                    value={sql}
                    onChange={e => {
                      setSql(e.target.value);
                      setSqlError(false);
                      setTestResultStatus(undefined);
                    }}
                    disabled={isReadOnly}
                  />

                  {isReadOnly === false && (
                    <Grid container justify="flex-end" alignItems="center">
                      {testResultStatus !== 201 && sqlError && (
                        <Typography variant="caption" className={classes.testResultDescriptionError}>
                          a SQL should be passed the test.
                        </Typography>
                      )}
                      {testResultStatus === 201 && <DoneIcon style={{ marginRight: '6px', color: green['A700'] }} />}
                      {testResultStatus !== 201 && testResultStatus !== undefined && <ClearIcon style={{ marginRight: '6px', color: red['A700'] }} />}
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.queryButton}
                        disabled={sql.length <= 0 || pendingTest}
                        onClick={async () => testSQL()}
                      >
                        TEST
                        {pendingTest && <CircularProgress size={24} className={classes.testButtonProgress} />}
                      </Button>
                    </Grid>
                  )}
                </Box>
                <Box paddingTop={5}>
                  <Typography variant="h6" component="h3" className={classes.sectionTitle}>
                    Test Results
                    {testResultStatus === 201 && testStories && (
                      <Typography
                        variant="caption"
                        className={classes.testResultDescription}
                      >{`(${testStories.length} stories have been selected.)`}</Typography>
                    )}
                    {testResultStatus !== 201 && testResultStatus !== undefined && (
                      <Typography variant="caption" className={classes.testResultDescriptionError}>
                        Oops! Please check your query again.
                      </Typography>
                    )}
                  </Typography>
                  {testResultStatus !== 201 && <Box className={classes.testResultNoResult}>The test query results will be shown.</Box>}
                  {testResultStatus === 201 && testStories && testStories.length === 0 && (
                    <Box className={classes.testResultNoResult}>There is no result at this time. 😕</Box>
                  )}
                  {testResultStatus === 201 && testStories && (
                    <List className={classes.testResultWrap}>
                      <StoryList
                        stories={testStories}
                        additionalInfo={true}
                        readonly={currentTrackingId ? true : false}
                        removable={false}
                        hideSearch={true}
                        onStoryGenreTextChange={(story, text) => {
                          const cloned = [...testStories];
                          const deepClonedStory = cloneDeep(story);
                          deepClonedStory.storyGenreText = text;

                          const replaced = replace(cloned, s => s.id === story.id && s.seasonId === story.seasonId, deepClonedStory);

                          setTestStories(replaced);
                        }}
                      />
                    </List>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default AutomatedCollectionView;
